/*
 * Presento Pronto: Personalisierung in der Kaufbox <pp-personalize> (blocks/pp-personalize.liquid).
 *
 * - Pflichtfelder sind echte required-Felder am Produktformular (form-Attribut). Horizon prueft vor der
 *   Raketen-Animation form.checkValidity(); fehlt etwas, startet weder die Rakete noch der Submit.
 *   Die Browser-Blase wird unterdrueckt, stattdessen: rote Markierung, Meldung, Wackeln, Fokus aufs erste Feld.
 * - Autosave: Eingaben (Text und Foto-URL) liegen in sessionStorage je Produkt und kommen nach Neuladen
 *   oder Zuruecknavigieren wieder. Gespeichert = gruen mit Haken und "So drucken wir es".
 *   Nach erfolgreichem Hinzufuegen zum Warenkorb wird alles geleert, die naechste Bestellung startet frisch.
 * - Textfelder: Emojis raus (die Produktionsschriften koennen sie nicht), Live-Vorschau in der Schrift aus font_settings.
 * - Foto: Datei waehlen, Ausschnitt im Seitenverhaeltnis der Variante festlegen, im Browser zuschneiden und als
 *   data-URL an die Weltmaschine hochladen. Die Antwort-URL landet in der Property.
 *   Waehrend des Uploads bleibt der Warenkorb-Button gesperrt.
 * - Zeigt die Panda-App ihr KI-Foto-Widget, sind alle Felder dieses Blocks deaktiviert (nicht im Formular).
 */
if (!customElements.get('pp-personalize')) {
  const EMOJI = /[\p{Extended_Pictographic}\u{FE0F}\u{200D}\u{20E3}\u{1F3FB}-\u{1F3FF}]/gu;
  const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_SIDE = 2400;
  const SAVE_DELAY = 600;
  const APP_WIDGET = '[id^="ai-custom-image-react-root"]';
  const CART_LINES_UPDATE = 'shopify:cart:lines-update';

  /** "3/4" -> 0.75, sonst null */
  const parseRatio = (value) => {
    const [a, b] = String(value || '')
      .split('/')
      .map((part) => parseFloat(part));
    return a > 0 && b > 0 ? a / b : null;
  };

  /* sessionStorage kann fehlen oder werfen (privates Fenster, blockierte Website-Daten) */
  const store = {
    get(key) {
      try {
        return sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        if (value) sessionStorage.setItem(key, value);
        else sessionStorage.removeItem(key);
      } catch {
        /* Autosave ist Komfort, kein Muss */
      }
    },
  };

  const shake = (field) => {
    field.removeAttribute('data-shake');
    void field.offsetWidth;
    field.setAttribute('data-shake', '');
    field.addEventListener('animationend', () => field.removeAttribute('data-shake'), { once: true });
  };

  class PhotoField {
    /** @param {HTMLElement} root @param {PpPersonalize} owner */
    constructor(root, owner) {
      this.root = root;
      this.owner = owner;
      this.fileInput = root.querySelector('[data-photo-file]');
      this.urlInput = root.querySelector('[data-photo-url]');
      this.frame = root.querySelector('[data-crop-frame]');
      this.img = root.querySelector('[data-crop-img]');
      this.zoomInput = root.querySelector('[data-crop-zoom]');
      this.thumb = root.querySelector('[data-photo-thumb]');
      this.status = root.querySelector('[data-photo-status]');
      this.error = root.querySelector('[data-error]');
      this.ratio = parseRatio(root.dataset.ratio);
      this.storageKey = owner.storageKey(this.urlInput.name);
      this.busy = false;
      this.objectUrl = null;
      this.state = { zoom: 1, cx: 0, cy: 0 };
      this.drag = null;

      this.fileInput.addEventListener('change', this.onFile);
      this.zoomInput.addEventListener('input', this.onZoom);
      this.frame.addEventListener('pointerdown', this.onPointerDown);
      this.frame.addEventListener('keydown', this.onKey);
      root.querySelector('[data-crop-apply]').addEventListener('click', this.onApply);
      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.frame);

      this.restore();
    }

    destroy() {
      this.resizeObserver.disconnect();
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    }

    get valid() {
      return !this.busy && this.urlInput.value !== '';
    }

    /** Fokusziel fuer Fehler: Zuschnitt, wenn offen, sonst die Dateiauswahl */
    get focusTarget() {
      return this.frame.offsetParent ? this.frame : this.fileInput;
    }

    stage(name) {
      for (const el of this.root.querySelectorAll('[data-photo-stage]')) {
        el.hidden = el.dataset.photoStage !== name;
      }
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = !message;
      this.root.toggleAttribute('data-invalid', Boolean(message));
    }

    /** Fertiges Foto anzeigen und merken */
    setUrl(url) {
      this.urlInput.value = url;
      store.set(this.storageKey, url);
      this.root.toggleAttribute('data-saved', Boolean(url));
      if (!url) return;
      this.thumb.src = url;
      this.status.textContent = this.owner.msg('uploaded');
      this.stage('done');
      this.showError('');
    }

    restore() {
      const url = store.get(this.storageKey);
      if (url) this.setUrl(url);
    }

    reset() {
      this.setUrl('');
      this.thumb.removeAttribute('src');
      this.status.textContent = '';
      this.showError('');
      this.stage('empty');
    }

    onFile = () => {
      const file = this.fileInput.files?.[0];
      if (!file) return;
      if (!IMAGE_TYPES.includes(file.type)) {
        this.showError(this.owner.msg('file'));
        this.fileInput.value = '';
        return;
      }

      this.showError('');
      this.setUrl('');
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = URL.createObjectURL(file);
      this.fileInput.value = '';

      this.img.onload = () => {
        const { naturalWidth: w, naturalHeight: h } = this.img;
        this.img.style.width = `${w}px`;
        this.img.style.height = `${h}px`;
        this.frame.style.setProperty('--pp-pz-ratio', String(this.ratio || w / h));
        this.state = { zoom: 1, cx: w / 2, cy: h / 2 };
        this.zoomInput.value = '1';
        this.stage('crop');
        this.layout();
        this.frame.focus({ preventScroll: true });
      };
      this.img.onerror = () => this.showError(this.owner.msg('file'));
      this.img.src = this.objectUrl;
    };

    /** Bild so legen, dass es den Rahmen immer ganz fuellt (cover), Mittelpunkt und Zoom aus state */
    layout() {
      const w = this.img.naturalWidth;
      const h = this.img.naturalHeight;
      const fw = this.frame.clientWidth;
      const fh = this.frame.clientHeight;
      if (!w || !h || !fw || !fh) return;

      const scale = Math.max(fw / w, fh / h) * this.state.zoom;
      const halfW = fw / 2 / scale;
      const halfH = fh / 2 / scale;
      this.state.cx = Math.min(Math.max(this.state.cx, halfW), w - halfW);
      this.state.cy = Math.min(Math.max(this.state.cy, halfH), h - halfH);
      this.scale = scale;

      const x = fw / 2 - this.state.cx * scale;
      const y = fh / 2 - this.state.cy * scale;
      this.img.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    }

    onZoom = () => {
      this.state.zoom = parseFloat(this.zoomInput.value) || 1;
      this.layout();
    };

    onPointerDown = (event) => {
      if (!this.scale) return;
      this.frame.setPointerCapture(event.pointerId);
      this.drag = { x: event.clientX, y: event.clientY, cx: this.state.cx, cy: this.state.cy };
      const move = (e) => {
        if (!this.drag) return;
        this.state.cx = this.drag.cx - (e.clientX - this.drag.x) / this.scale;
        this.state.cy = this.drag.cy - (e.clientY - this.drag.y) / this.scale;
        this.layout();
      };
      const up = () => {
        this.drag = null;
        this.frame.removeEventListener('pointermove', move);
        this.frame.removeEventListener('pointerup', up);
        this.frame.removeEventListener('pointercancel', up);
      };
      this.frame.addEventListener('pointermove', move);
      this.frame.addEventListener('pointerup', up);
      this.frame.addEventListener('pointercancel', up);
    };

    onKey = (event) => {
      const step = 12 / (this.scale || 1);
      const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      const move = moves[event.key];
      if (!move) return;
      event.preventDefault();
      this.state.cx += move[0];
      this.state.cy += move[1];
      this.layout();
    };

    crop() {
      const fw = this.frame.clientWidth;
      const fh = this.frame.clientHeight;
      const sw = fw / this.scale;
      const sh = fh / this.scale;
      const sx = this.state.cx - sw / 2;
      const sy = this.state.cy - sh / 2;
      const factor = Math.min(1, MAX_SIDE / Math.max(sw, sh));

      const canvas = document.createElement('canvas');
      canvas.width = Math.round(sw * factor);
      canvas.height = Math.round(sh * factor);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(this.img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', 0.9);
    }

    onApply = async () => {
      if (this.busy || !this.scale) return;
      const dataUrl = this.crop();

      this.showError('');
      this.thumb.src = dataUrl;
      this.status.textContent = this.owner.msg('busy');
      this.stage('done');
      this.setBusy(true);

      try {
        const response = await fetch(this.owner.dataset.uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ image: dataUrl }),
        });
        const data = response.ok ? await response.json() : null;
        if (!data?.url) throw new Error(`Upload ${response.status}`);

        this.setUrl(data.url);
        this.thumb.src = dataUrl;
      } catch (error) {
        console.warn('[pp-personalize] Upload fehlgeschlagen', error);
        this.setUrl('');
        this.status.textContent = '';
        this.stage('crop');
        this.showError(this.owner.msg('uploadFailed'));
      } finally {
        this.setBusy(false);
      }
    };

    setBusy(busy) {
      this.busy = busy;
      this.root.toggleAttribute('data-busy', busy);
      this.owner.lockCart();
    }
  }

  class PpPersonalize extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.productForm = this.form?.closest('product-form-component') || null;
      this.textInputs = [...this.querySelectorAll('[data-text-input]')];
      this.preview = this.querySelector('[data-preview]');
      this.lockedButtons = new Map();
      this.saveTimers = new Map();
      this.invalidBurst = null;

      if (this.appWidgetActive()) {
        this.photos = [];
        this.deactivate();
        return;
      }

      this.photos = [...this.querySelectorAll('[data-photo]')].map((el) => new PhotoField(el, this));

      for (const input of this.textInputs) {
        input.addEventListener('input', this.onText);
        input.addEventListener('blur', this.onBlur);
        input.addEventListener('invalid', this.onInvalid);
        const saved = store.get(this.storageKey(input.name));
        if (saved) {
          input.value = saved;
          this.markSaved(input, true);
        }
      }
      for (const photo of this.photos) photo.urlInput.addEventListener('invalid', this.onInvalid);

      window.addEventListener('submit', this.onSubmit, true);
      this.productForm?.addEventListener(CART_LINES_UPDATE, this.onCartUpdate);
      this.setupPreview();
    }

    disconnectedCallback() {
      window.removeEventListener('submit', this.onSubmit, true);
      this.productForm?.removeEventListener(CART_LINES_UPDATE, this.onCartUpdate);
      for (const input of this.textInputs) {
        input.removeEventListener('input', this.onText);
        input.removeEventListener('blur', this.onBlur);
        input.removeEventListener('invalid', this.onInvalid);
      }
      for (const timer of this.saveTimers.values()) clearTimeout(timer);
      for (const photo of this.photos) {
        photo.urlInput.removeEventListener('invalid', this.onInvalid);
        photo.destroy();
      }
      this.previewObserver?.disconnect();
    }

    msg(key) {
      const name = `msg${key.charAt(0).toUpperCase()}${key.slice(1)}`;
      return this.dataset[name] || '';
    }

    storageKey(name) {
      return `${this.dataset.storageKey}:${name}`;
    }

    appWidgetActive() {
      const scope = this.closest('.product-details') || this.closest('[id^="shopify-section"]') || document;
      return Boolean(scope.querySelector(APP_WIDGET));
    }

    /** Felder aus dem Formular nehmen: deaktivierte Felder schickt FormData nicht mit und prueft required nicht */
    deactivate() {
      for (const el of this.querySelectorAll('input')) el.disabled = true;
      this.hidden = true;
    }

    /* ---------- Text: Emojis raus, Autosave, gruen ---------- */

    onText = (event) => {
      const input = event.target;
      const cleaned = input.value.replace(EMOJI, '');
      if (cleaned !== input.value) {
        const caret = Math.max(0, (input.selectionStart ?? cleaned.length) - (input.value.length - cleaned.length));
        input.value = cleaned;
        input.setSelectionRange(caret, caret);
      }
      if (cleaned.trim()) this.setTextError(input, '');
      this.markSaved(input, false);
      this.updatePreview();

      clearTimeout(this.saveTimers.get(input));
      this.saveTimers.set(input, setTimeout(() => this.saveText(input), SAVE_DELAY));
    };

    onBlur = (event) => {
      clearTimeout(this.saveTimers.get(event.target));
      this.saveText(event.target);
    };

    saveText(input) {
      const value = input.value.trim();
      store.set(this.storageKey(input.name), value);
      this.markSaved(input, Boolean(value));
    }

    markSaved(input, saved) {
      input.toggleAttribute('data-saved', saved);
      const ok = input.parentElement.querySelector('[data-ok]');
      if (!ok) return;
      ok.hidden = !saved;
      if (saved) ok.querySelector('[data-ok-value]').textContent = `„${input.value.trim()}“`;
    }

    setTextError(input, message) {
      const error = input.parentElement.querySelector('[data-error]');
      if (error) {
        error.textContent = message;
        error.hidden = !message;
      }
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }

    /* ---------- Pruefung ---------- */

    /**
     * Natives invalid (aus Horizons checkValidity beim Klick und aus dem Submit): Browser-Blase aus,
     * eigene Meldung an. Alle invalid-Events eines Klicks werden gesammelt und einmal gemeldet.
     */
    onInvalid = (event) => {
      event.preventDefault();
      const input = event.target;
      const photo = this.photos.find((p) => p.urlInput === input);

      if (photo) photo.showError(this.msg(photo.busy ? 'uploading' : 'photo'));
      else this.setTextError(input, this.msg('text'));

      if (!this.invalidBurst) {
        this.invalidBurst = new Set();
        setTimeout(() => this.reportInvalid(), 0);
      }
      this.invalidBurst.add(photo ? photo.root : input.closest('.pp-pz__field'));
    };

    reportInvalid() {
      const fields = [...(this.invalidBurst || [])];
      this.invalidBurst = null;
      if (!fields.length) return;

      for (const field of fields) shake(field);

      const first = fields.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))[0];
      const photo = this.photos.find((p) => p.root === first);
      const target = photo ? photo.focusTarget : first.querySelector('[data-text-input]');
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      first.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
      target?.focus({ preventScroll: true });
    }

    /** Rueckfallnetz, falls ein Weg ohne Formular-Pruefung absendet (z. B. form.submit-Varianten) */
    onSubmit = (event) => {
      if (!this.form || event.target !== this.form || !this.isConnected) return;
      if (this.appWidgetActive()) return;

      for (const input of this.textInputs) input.value = input.value.trim();
      const invalid = [...this.textInputs, ...this.photos.map((p) => p.urlInput)].filter((el) => !el.checkValidity());
      const busy = this.photos.some((p) => p.busy);
      if (!invalid.length && !busy) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    };

    /** Erfolgreich im Warenkorb: Eingaben leeren, die naechste Bestellung startet frisch */
    onCartUpdate = (event) => {
      event.promise
        ?.then((result) => {
          if (result?.detail?.didError || result?.detail?.source !== 'product-form-component') return;
          this.reset();
        })
        .catch(() => {});
    };

    reset() {
      for (const input of this.textInputs) {
        clearTimeout(this.saveTimers.get(input));
        input.value = '';
        store.set(this.storageKey(input.name), '');
        this.markSaved(input, false);
        this.setTextError(input, '');
      }
      for (const photo of this.photos) photo.reset();
      this.updatePreview();
    }

    /** Warenkorb-Buttons sperren, solange ein Foto hochlaedt; vorherigen Zustand merken */
    lockCart() {
      const busy = this.photos.some((photo) => photo.busy);
      const scope = this.productForm || this.form;
      if (!scope) return;
      const buttons = scope.querySelectorAll('button[type="submit"], [ref="addToCartButton"]');

      for (const button of buttons) {
        if (busy && !this.lockedButtons.has(button)) {
          this.lockedButtons.set(button, button.disabled);
          button.disabled = true;
        } else if (!busy && this.lockedButtons.has(button)) {
          button.disabled = this.lockedButtons.get(button);
          this.lockedButtons.delete(button);
        }
      }
    }

    /* ---------- Vorschau ---------- */

    setupPreview() {
      if (!this.preview) return;
      this.previewText = this.preview.querySelector('[data-preview-text]');
      this.placeholder = this.previewText.textContent;

      const { fontName, fontUri, box } = this.preview.dataset;
      if (fontName && fontUri && 'FontFace' in window) {
        const family = `pp-pz-${fontName.replace(/[^a-z0-9-]/gi, '')}`;
        new FontFace(family, `url("${fontUri}")`)
          .load()
          .then((face) => {
            document.fonts.add(face);
            this.previewText.style.fontFamily = `"${family}", var(--font-body--family)`;
          })
          .catch(() => {
            /* Schrift nicht ladbar (z. B. CORS): Vorschau in der Theme-Schrift */
          });
      }

      const img = this.preview.querySelector('[data-preview-img]');
      if (img && box) {
        const [x, y, w, h] = box.split(',').map(Number);
        const place = () => {
          if (!img.naturalWidth) return;
          const k = img.clientWidth / img.naturalWidth;
          Object.assign(this.previewText.style, {
            left: `${x * k}px`,
            top: `${y * k}px`,
            width: `${w * k}px`,
            height: `${h * k}px`,
            right: 'auto',
            bottom: 'auto',
            fontSize: `${Math.max(12, h * k * 0.18)}px`,
          });
        };
        if (img.complete) place();
        img.addEventListener('load', place);
        this.previewObserver = new ResizeObserver(place);
        this.previewObserver.observe(img);
      }

      this.updatePreview();
    }

    updatePreview() {
      if (!this.previewText) return;
      const value = this.textInputs[0]?.value.trim() || '';
      this.previewText.textContent = value || this.placeholder;
      this.previewText.toggleAttribute('data-empty', !value);
    }
  }

  customElements.define('pp-personalize', PpPersonalize);
}
